import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { capitalizeFirstLetter } from '../../utils/string';
import { getColorFromCode } from '../../common/constants';
import { SimpleContent, SimpleContentDescription, UniverseSections } from '../../generated/contentful-generated-types';
import { isSimpleContent } from '../../utils/contentful';
import { useRichText } from '../../hooks/richtext';
import EntryLink from '../EntryLink';

interface Props {
  section: UniverseSections;
}

type ExtendedSimpleContent = SimpleContent & {
  simpleContentDescription: SimpleContentDescription;
};

export const HighlightImage: FunctionComponent<Props> = ({ section }) => {
  const items = section?.itemsCollection?.items;

  const contentItem = items?.find((item): item is ExtendedSimpleContent => !!item && isSimpleContent(item));

  // do not display anything if no simplecontent
  if (!contentItem) {
    return null;
  }

  const { title, simpleContentDescription: description, buttonLabel, media, charterTheme, relatedContent } = contentItem;

  return (
    <StyledSection>
      <StyledCardGrid charterTheme={charterTheme}>
        <StyledMainCardImage src={media?.url}></StyledMainCardImage>
        <StyledMainCardText>
          <StyledContent>
            <StyledTitle>{title && capitalizeFirstLetter(title)}</StyledTitle>
            <StyledDescription>{description && useRichText(description)}</StyledDescription>
            {buttonLabel && (
              <StyledCTA entry={relatedContent} className="focus-button">
                {buttonLabel}
              </StyledCTA>
            )}
          </StyledContent>
        </StyledMainCardText>
      </StyledCardGrid>
    </StyledSection>
  );
};

const StyledSection = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  z-index: 1;

  padding-bottom: 0;

  @media (max-width: 576px) {
    margin-top: 32px;
    padding-bottom: 0px;
  }
  @media (max-width: 375px) {
    margin-top: 0px;
  }
`;

const StyledCardGrid = styled.div<{ charterTheme: string }>`
  display: flex;
  flex-direction: row;
  column-gap: 0px;
  background-color: ${props => getColorFromCode(props.charterTheme, 'transparent')};
  @media (max-width: 576px) {
    flex-direction: column-reverse;
    align-items: start;
    gap: 4px;
  }
`;

const StyledMainCardImage = styled.div<{ src: string }>`
  display: flex;
  min-width: calc(50% - 16px);
  flex-shrink: 1;

  // padding: 32px;
  position: relative;
  background-image: url(${props => props.src});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  @media (max-width: 576px) {
    height: 206px;
    min-width: 100%;
    padding: 16px;
  }
`;

const StyledMainCardText = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-shrink: 1;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 24px;
  row-gap: 16px;
  padding: 80px 32px;
  @media (max-width: 768px) {
    gap: 4px;
    padding: 32px 32px;
  }
  @media (max-width: 576px) {
    gap: 4px;
    padding: 32px 24px;
  }
`;

const StyledContent = styled.div`
  padding: 0px 15%;
  @media (max-width: 768px) {
    padding: 0px 5%;
  }
  @media (max-width: 380px) {
    padding: 0px;
  }
`;

const StyledTitle = styled.div`
  font-size: 24px;
  font-family: 'Styrene Medium';
  line-height: 1.5;
  color: ${getColorFromCode('exa black', 'rgba(0, 200, 130, 0.3)')};
  margin-bottom: 8px;

  @media (max-width: 576px) {
    font-size: 18px;
    left: 8px;
    top: 8px;
  }
  @media (max-width: 380) {
    font-size: 20px;
  }
`;

const StyledDescription = styled.div`
  margin-bottom: 24px;
  font-size: 24px;
  line-height: 1.5;
  font-size: 14px;
  @media (max-width: 380px) {
    font-size: 14px;
  }
`;

const buttonHover = `
&:hover {
  background: var(--black-400, #676767);
  color: var(--white, #fff);
  transition: all 0.3s ease-in-out;
}
`;
const StyledCTA = styled(EntryLink)`
  padding: 12px 30px;
  display: block;
  width: fit-content;
  border-radius: 4px;

  box-shadow: 2px 2px 10px 0px rgba(50, 50, 50, 0.2);
  cursor: pointer;
  background: ${getColorFromCode('exa black', '#323232')};
  color: var(--white, #fff);
  font-family: 'Styrene Medium';
  ${buttonHover}
  font-size: 14px;

  @media (max-width: 576px) {
    width: calc(100% - 32px);
    margin: 0 auto;
    text-align: center;
  }
  @media (max-width: 380px) {
    width: 100%;
    margin: 0 auto;
    text-align: center;
  }
`;
